import { flushSync } from 'react-dom'

/**
 * Transitions between screens, by the View Transition API: the browser takes
 * a picture of the old screen, the change happens underneath it, and the two
 * pictures animate into each other. There is never a moment where half the
 * page is old and half new, and nothing waits on a timer.
 *
 * Two kinds, styled in styles.css by type:
 * - `page`: a navigation to another page (the router, `defaultViewTransition`).
 * - `tab`: another section of the same page (tabs, a plan's pills).
 * The shell (top bar, menu, tab bar, toasts) is left out of both: it stays
 * still while the content changes. A browser without the API (or without
 * transition types) just changes; so does anyone who asks for less motion.
 */

/**
 * Search keys that choose a screen rather than filter one: Services' tab, a
 * song and its arrangement, a service type's plans. A change to one of them
 * is a new screen (a `page` transition) though the path stays the same; a
 * change to anything else (a search box, a date range) is not.
 */
const SCREEN_KEYS = ['tab', 'song', 'arrangement', 'type'] as const

/** The router's choice for a navigation: `page` for a new screen, none for a filter. */
export function navigationTypes({
  fromLocation,
  toLocation,
  pathChanged,
}: {
  fromLocation?: { search: unknown }
  toLocation: { search: unknown }
  pathChanged: boolean
}): string[] | false {
  if (pathChanged) return ['page']
  const before = (fromLocation?.search ?? {}) as Record<string, unknown>
  const after = (toLocation.search ?? {}) as Record<string, unknown>
  return SCREEN_KEYS.some((key) => before[key] !== after[key]) ? ['page'] : false
}

const supported = () =>
  typeof document !== 'undefined' &&
  typeof document.startViewTransition === 'function' &&
  typeof CSS !== 'undefined' &&
  CSS.supports('selector(:active-view-transition-type(a))')

const calm = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Switches a page's section with a `tab` transition. `update` may change
 * state (applied at once, inside the transition) or navigate (its promise is
 * awaited, so the new section is what the browser pictures).
 */
export function switchSection(update: () => unknown): void {
  if (!supported() || calm()) {
    void update()
    return
  }
  document.startViewTransition({
    update: async () => {
      let result: unknown
      flushSync(() => {
        result = update()
      })
      if (result instanceof Promise) await result
    },
    types: ['tab'],
  })
}

/** The latest transition (any kind: a page's or a section's), and when it ended. */
let latest: ViewTransition | null = null
let endedAt = Number.NEGATIVE_INFINITY

/**
 * Taps during a transition. While one runs, the browser hits nothing under
 * its pictures: a tap lands on the document itself and is lost (the next
 * pill, tapped before the last section finished fading in, did nothing but
 * flinch). So a tap then ends the transition at once and goes on to whatever
 * is under the finger. A tap pressed during one and let go just after it
 * ended has the same fate, so the last moments count too. Installed once,
 * at boot.
 */
export function keepTapsDuringTransitions(): void {
  if (!supported()) return
  const start = document.startViewTransition.bind(document)
  document.startViewTransition = ((update?: Parameters<typeof start>[0]) => {
    const transition = start(update)
    latest = transition
    endedAt = Number.POSITIVE_INFINITY
    // Skipped (by the next tap) is not a failure.
    transition.ready.catch(() => undefined)
    void transition.finished.then(() => {
      if (latest === transition) endedAt = performance.now()
    })
    return transition
  }) as typeof document.startViewTransition
  document.addEventListener(
    'click',
    (event) => {
      const transition = latest
      const lately = performance.now() - endedAt < 300
      if (!transition || !lately || event.target !== document.documentElement) return
      event.preventDefault()
      event.stopPropagation()
      const { clientX: x, clientY: y } = event
      transition.skipTransition()
      // Once the page is drawn again (a frame or two after the pictures go).
      const send = (tries: number) => {
        const target = document.elementFromPoint(x, y)
        if (target instanceof HTMLElement && target !== document.documentElement) target.click()
        else if (tries > 0) requestAnimationFrame(() => send(tries - 1))
      }
      void transition.finished.then(() => requestAnimationFrame(() => send(3)))
    },
    true,
  )
}
